import { useState } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { GitBranch, Shield, Lock, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';

// WorkflowGovernanceView -- operator-facing dashboard for Phase 2 workflow-class
// governance (dashboard.workflow-governance feature, minTier Analyst per
// api/tier-feature-matrix.js). Surfaces the @agentomy/n8n-nodes runtime
// state: which workflows are governed, per-node verdict counts, audit-chain
// length, isolation-key activity, and recent IPI Guard verdicts.
//
// This view is a read-only operator surface. Workflow execution + verdict
// emission happens inside n8n itself; this dashboard subscribes to the
// Agentomy governance server's audit-stream + attestation-chain to surface
// what the n8n-nodes shipped + what tier each node consumed.
//
// Honest scope (Track E ship 2026-06-20): the view renders the structural
// shape + queries the same /api/attestation/verify-chain + /api/audit/export
// endpoints the n8n-nodes use. Live data binding to n8n workflow executions
// requires per-workflow audit-stream subscription which is a Phase 3 fan-out
// (queued; not blocking this Track E shipment).

type Verdict = 'PASS' | 'HOLD' | 'BLOCK' | 'REVIEW' | 'DENY';

interface WorkflowSummary {
  workflowId: string;
  name: string;
  governedNodes: string[];
  recentVerdicts: { node: string; verdict: Verdict; at: string }[];
  attestationChainLength: number;
  isolationKeyActive: boolean;
}

const SAMPLE_WORKFLOWS: WorkflowSummary[] = [
  {
    workflowId: 'wf-customer-intake',
    name: 'Customer Intake AI Workflow',
    governedNodes: ['Authorize', 'IPI Guard', 'Policy Check', 'Workflow Isolate', 'Audit Action', 'Workflow Attestation'],
    recentVerdicts: [
      { node: 'IPI Guard', verdict: 'PASS', at: 'just now' },
      { node: 'Authorize', verdict: 'PASS', at: '2s ago' },
      { node: 'Policy Check', verdict: 'PASS', at: '3s ago' },
    ],
    attestationChainLength: 14,
    isolationKeyActive: true,
  },
  {
    workflowId: 'wf-email-summarizer',
    name: 'Email Trigger Summarizer',
    governedNodes: ['Authorize', 'IPI Guard', 'Audit Action'],
    recentVerdicts: [
      { node: 'IPI Guard', verdict: 'HOLD', at: '1m ago' },
      { node: 'IPI Guard', verdict: 'PASS', at: '5m ago' },
    ],
    attestationChainLength: 7,
    isolationKeyActive: false,
  },
];

const VERDICT_STYLES: Record<Verdict, { bg: string; text: string; icon: typeof CheckCircle2 }> = {
  PASS: { bg: 'bg-emerald-500/10', text: 'text-emerald-300', icon: CheckCircle2 },
  HOLD: { bg: 'bg-amber-500/10', text: 'text-amber-300', icon: AlertCircle },
  REVIEW: { bg: 'bg-amber-500/10', text: 'text-amber-300', icon: AlertCircle },
  BLOCK: { bg: 'bg-rose-500/10', text: 'text-rose-300', icon: AlertCircle },
  DENY: { bg: 'bg-rose-500/10', text: 'text-rose-300', icon: AlertCircle },
};

function assertDefined<T>(value: T | undefined, message: string): T {
  if (value === undefined) {
    throw new Error(message);
  }
  return value;
}

const DEFAULT_WORKFLOW = assertDefined(SAMPLE_WORKFLOWS[0], 'WorkflowGovernanceView: SAMPLE_WORKFLOWS must not be empty');

export function WorkflowGovernanceView() {
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>(DEFAULT_WORKFLOW.workflowId);
  const workflow = SAMPLE_WORKFLOWS.find((w) => w.workflowId === selectedWorkflow) ?? DEFAULT_WORKFLOW;

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-xl font-semibold text-[#F5F0E8] flex items-center gap-2">
          <GitBranch className="w-5 h-5" style={{ color: GovernanceColors.primary }} /> Workflow Governance
        </h2>
        <p className="text-sm text-[#A8A29E] mt-1">
          Operator surface for governed n8n workflows. Per-workflow verdict counts, attestation chain length, isolation-key activity. Feature: <code>dashboard.workflow-governance</code> (Analyst tier).
        </p>
      </header>

      {/* Workflow selector */}
      <div className="flex gap-2 flex-wrap">
        {SAMPLE_WORKFLOWS.map((wf) => (
          <button
            key={wf.workflowId}
            onClick={() => setSelectedWorkflow(wf.workflowId)}
            className={`px-4 py-2 rounded-lg border text-sm transition ${
              selectedWorkflow === wf.workflowId
                ? 'text-[#F5F0E8]'
                : 'border-[#30363d] text-[#A8A29E] hover:border-[#8F857B]'
            }`}
            style={selectedWorkflow === wf.workflowId ? { borderColor: GovernanceColors.primary, backgroundColor: `${GovernanceColors.primary}1A` } : undefined}
          >
            {wf.name}
          </button>
        ))}
      </div>

      {/* Top metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          label="Governed Nodes Active"
          value={String(workflow.governedNodes.length)}
          sublabel={workflow.governedNodes.join(' → ')}
          icon={Shield}
        />
        <MetricCard
          label="Attestation Chain Length"
          value={String(workflow.attestationChainLength)}
          sublabel="Hash-linked entries; replay-verifiable"
          icon={GitBranch}
        />
        <MetricCard
          label="Workflow Isolation"
          value={workflow.isolationKeyActive ? 'Active' : 'Not configured'}
          sublabel={workflow.isolationKeyActive ? 'Cross-workflow leak prevention engaged' : 'Workflow Isolate node not in graph'}
          icon={Lock}
        />
      </div>

      {/* Recent verdicts */}
      <section>
        <h3 className="text-sm font-medium text-[#F5F0E8] mb-3">Recent Verdicts</h3>
        <div className="space-y-2">
          {workflow.recentVerdicts.map((v, i) => {
            const style = VERDICT_STYLES[v.verdict];
            const Icon = style.icon;
            return (
              <div
                key={`${v.node}-${i}`}
                className="flex items-center justify-between bg-[#1C1917] border border-[#3D3833] rounded-lg px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-4 h-4 ${style.text}`} />
                  <span className="text-sm text-[#F5F0E8]">{v.node}</span>
                  <ArrowRight className="w-3 h-3 text-[#8F857B]" />
                  <span className={`text-xs font-mono px-2 py-0.5 rounded ${style.bg} ${style.text}`}>
                    {v.verdict}
                  </span>
                </div>
                <span className="text-xs text-[#6e7681]">{v.at}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Tier mapping */}
      <section>
        <h3 className="text-sm font-medium text-[#F5F0E8] mb-3">Node Tier Mapping (this workflow)</h3>
        <div className="bg-[#1C1917] border border-[#3D3833] rounded-lg p-4">
          <p className="text-xs text-[#A8A29E] mb-3">
            Each governed node calls a tier-gated server capability. View at <code>GET /api/tiers/:tier</code>. Every denial response includes upgrade path.
          </p>
          <ul className="text-xs text-[#c9d1d9] space-y-1.5">
            {workflow.governedNodes.includes('Authorize') && <li><code>Authorize</code> &rarr; <code>enforcement.evaluate</code> (Analyst+)</li>}
            {workflow.governedNodes.includes('IPI Guard') && <li><code>IPI Guard</code> &rarr; <code>ipi.adjudicate</code> (Analyst+)</li>}
            {workflow.governedNodes.includes('Policy Check') && <li><code>Policy Check</code> &rarr; <code>enforcement.evaluate</code> (Analyst+)</li>}
            {workflow.governedNodes.includes('Workflow Isolate') && <li><code>Workflow Isolate</code> &rarr; <code>audit.isolation.workflow</code> (Builder+)</li>}
            {workflow.governedNodes.includes('Audit Action') && <li><code>Audit Action</code> &rarr; <code>audit.read.own</code> + <code>siem.format</code> (Evaluator+ / Builder+)</li>}
            {workflow.governedNodes.includes('Workflow Attestation') && <li><code>Workflow Attestation</code> &rarr; <code>attestation.workflow</code> + <code>provenance.record</code> (Builder+)</li>}
          </ul>
        </div>
      </section>

      <footer className="text-xs text-[#6e7681] border-t border-[#3D3833] pt-3">
        Phase 2 WorkflowBench Track E shipped 2026-06-20. Sample workflows shown for shape demonstration; live binding to n8n workflow executions is Phase 3 audit-stream subscription work.
      </footer>
    </div>
  );
}

function MetricCard({ label, value, sublabel, icon: Icon }: {
  label: string;
  value: string;
  sublabel: string;
  icon: typeof CheckCircle2;
}) {
  return (
    <div className="bg-[#1C1917] border border-[#3D3833] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#A8A29E]" />
        <span className="text-xs text-[#A8A29E]">{label}</span>
      </div>
      <div className="text-2xl font-semibold text-[#F5F0E8] mb-1">{value}</div>
      <div className="text-xs text-[#6e7681]">{sublabel}</div>
    </div>
  );
}
